import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };

function Paragraphs({ text, className }) {
  return (
    <div className={`space-y-5 ${className}`}>
      {(text || "")
        .split("\n")
        .filter((p) => p.trim())
        .map((p, i) => (
          <p key={i}>{p}</p>
        ))}
    </div>
  );
}

export default function About() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    api
      .get("/content/about")
      .then((r) => setContent(r.data))
      .catch(() => setContent({}));
  }, []);

  if (content === null) {
    return <div className="px-6 py-32 text-soot">Loading our story…</div>;
  }

  const story = content.about_story || {};
  const owner = content.about_owner || {};
  const mumkey = content.about_mumkey || {};

  return (
    <div data-testid="about-page">
      {/* BRAND STORY */}
      <section className="px-6 py-16 md:px-12 lg:px-20 lg:py-24">
        <div className="mx-auto max-w-[1400px]">
          <motion.p variants={fadeUp} initial="hidden" animate="show" className="text-xs font-bold uppercase tracking-[0.25em] text-soot">
            About Hunar Designs
          </motion.p>
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.15 }}
            data-testid="about-story-title"
            className="mt-4 max-w-3xl font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl md:text-7xl"
          >
            {story.title}
          </motion.h1>

          <div className="mt-16 grid gap-14 lg:grid-cols-12 lg:items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5"
            >
              <div className="overflow-hidden rounded-t-[200px] md:rounded-t-[240px]">
                {story.image && (
                  <img src={story.image} alt="The Hunar Designs story" data-testid="about-story-image" className="aspect-[3/4] w-full object-cover" />
                )}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="lg:col-span-7"
              data-testid="about-story-section"
            >
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-soot">The Brand Story</p>
              <Paragraphs text={story.text} className="mt-6 max-w-xl text-base leading-relaxed text-soot" />
              <Link
                to="/shop"
                data-testid="about-shop-link"
                className="group mt-10 inline-flex items-center gap-3 rounded-full bg-obsidian px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-bone transition-colors duration-300 hover:bg-olive"
              >
                Shop the Collections
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OWNER */}
      <section className="bg-linen px-6 py-20 md:px-12 lg:px-20 lg:py-28" data-testid="about-owner-section">
        <div className="mx-auto grid max-w-[1200px] gap-14 lg:grid-cols-12 lg:items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4"
          >
            <div className="overflow-hidden rounded-full border-8 border-bone">
              {owner.image && (
                <img src={owner.image} alt="The owner of Hunar Designs" data-testid="about-owner-image" className="aspect-square w-full object-cover" />
              )}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-8"
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-soot">Meet the Owner</p>
            <h2 data-testid="about-owner-title" className="mt-4 max-w-xl font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
              {owner.title}
            </h2>
            <Paragraphs text={owner.text} className="mt-6 max-w-xl text-base leading-relaxed text-soot" />
          </motion.div>
        </div>
      </section>

      {/* MUMKEY */}
      <section className="bg-[#FAF1ED] px-6 py-20 md:px-12 lg:px-20 lg:py-28" data-testid="about-mumkey-section">
        <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12 lg:items-center">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1 lg:col-span-7"
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#B76E79]">Mumkey · by Hunar Designs</p>
            <h2 data-testid="about-mumkey-title" className="mt-4 max-w-xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#4A2C2A] sm:text-5xl md:text-6xl">
              {mumkey.title}
            </h2>
            <Paragraphs text={mumkey.text} className="mt-6 max-w-xl text-base leading-relaxed text-[#7A5A55]" />
            <Link
              to="/mumkey"
              data-testid="about-mumkey-link"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[#B76E79] px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#9E5B66]"
            >
              Explore Mumkey
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="order-1 lg:order-2 lg:col-span-5"
          >
            <div className="overflow-hidden rounded-t-[999px] border-8 border-[#F3DEDA]">
              {mumkey.image && (
                <img src={mumkey.image} alt="Mumkey maternity wear" data-testid="about-mumkey-image" className="aspect-[3/4] w-full object-cover" />
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
